import axios, { AxiosResponse } from 'axios';
import {
  Operation,
  GameResultDto,
  SiteConfigDto,
  SitePublicData,
  CommonDataConfig,
  GameExperienceDto,
  ShopperExperienceDto,
  GameScreensConfiguration,
  SiteExperienceConfiguration,
} from '@luckycart/xpn-types';

import { Ticket } from '../../common-types/Ticket.type';

export interface AxiosFetchSiteDataResponse extends SiteConfigDto {
  err?: string
}

export interface AxiosFetchGameExperiencesResponse extends ShopperExperienceDto {
  err?: string,
}

export interface AxiosFetchGameResultResponse extends GameResultDto {
  err?: string,
}
type FetchGameResultResponse = GameResultDto;

interface PreviewResponse {
  gameConfig: {
    screens: GameScreensConfiguration,
  },
  siteConfig: {
    screens: SiteExperienceConfiguration,
    commons: CommonDataConfig,
  }
  ticket: Ticket,
  operation: Pick<Operation, '_id' | 'legal'>,
  site: SitePublicData,
}

export interface AxiosFetchGameExperienceResponse extends GameExperienceDto {
  err?: string;
}

export type FetchGameExperienceResponse = GameExperienceDto

export class ApiService {
  public readonly host: string;

  public readonly hostGameExperience: string;

  // eslint-disable-next-line no-magic-numbers
  public static readonly fetchGameRetryCount = 4;

  // eslint-disable-next-line no-magic-numbers
  private static readonly waitingPull = 500;

  public constructor () {
    this.host = process.env.REACT_APP_API_BASE_URL as string;
    this.hostGameExperience = process.env.REACT_APP_GAME_EXPERIENCE_BASE_URL as string;
  }

  public async fetchGameExperience (
    shopperId: string,
    siteKey: string,
    operationId: string,
    experienceId: string
  ): Promise<FetchGameExperienceResponse | null> {
    try{
      const url = `${this.hostGameExperience}/v1/game-experience`;
      const params = {
        shopperId,
        siteKey,
        operationId,
        experienceId,
      };
      const response: AxiosResponse<AxiosFetchGameExperienceResponse> = await axios.get(url, { params });
      if (response.data.err !== undefined) {
        throw new Error(response.data.err);
      }
      return response.data;
    } catch {
      return null;
    }
  }

  public async fetchSiteData (siteKey: string): Promise<AxiosFetchSiteDataResponse> {
    const url = `${this.hostGameExperience}/v1/site-config/`;
    const params = {
      siteKey,
    };
    const response: AxiosResponse<AxiosFetchSiteDataResponse> = await axios.get(url, { params });
    if (response.data.err !== undefined) {
      throw new Error(response.data.err);
    }
	
    return response.data;
  }

  public fetchGameResult (experienceId: string, shopperId: string, siteKey: string): Promise<FetchGameResultResponse> {
    const url = `${this.hostGameExperience}/v1/game-result`;
    const params = { shopperId, siteKey, experienceId };
    return axios.get(url, { params }).then((response: AxiosResponse<AxiosFetchGameResultResponse>) => {
      if (response.data.err !== undefined) {
        throw new Error(response.data.err);
      }
      return response.data;
    });
  }

  public async fetchGamesList (shopperId: string, siteKey: string): Promise<ShopperExperienceDto[]> {
    const url = `${this.hostGameExperience}/v1/game-experiences`;
    const params = {
      shopperId,
      siteKey,
    };

    for (let retryNumber = ApiService.fetchGameRetryCount; retryNumber > 0; retryNumber -= 1) {
      // eslint-disable-next-line no-await-in-loop
      const response: AxiosResponse<AxiosFetchGameExperiencesResponse> = await axios.get(url, { params });

      if (response.data.err !== undefined) {
        throw new Error(response.data.err);
      }

      if (Array.isArray(response.data) && response.data.length > 0) {
        return response.data;
      }

      // eslint-disable-next-line no-await-in-loop
      await new Promise<void>((resolve) => {
        setTimeout(() => resolve(), ApiService.waitingPull);
      });
    }
    return [];
  }

  public fetchPreviewData (operationId: string): Promise<PreviewResponse> {
    const url = `${this.host}/v3/previewConfig/${operationId}`;
    return axios.get(url).then((response: AxiosResponse<PreviewResponse>) => {
      return response.data;
    });
  }
}

export const apiServiceInstance = new ApiService();
